// src/components/soul-file/SoulFileDashboard.tsx
// The Soul File - A beautiful, living portrait of the user's transformation journey

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Heart,
  Target,
  TrendingUp,
  Star,
  Flame,
  BookOpen,
  ChevronRight,
  Plus,
  Edit3,
  Check,
  X,
  Award,
  Zap
} from 'lucide-react';
import { useSoulFile } from '../../hooks/useSoulFile';
import type { TransformationPhase, ManifestationCategory } from '../../types/mystara';

const PHASE_CONFIG: Record<TransformationPhase, { 
  name: string; 
  emoji: string; 
  color: string;
  bgColor: string;
  description: string;
}> = {
  illuminate: {
    name: 'Illuminate',
    emoji: '💡',
    color: 'from-amber-400 to-orange-400',
    bgColor: 'bg-amber-50',
    description: 'Seeing yourself clearly'
  },
  release: {
    name: 'Release',
    emoji: '🦋',
    color: 'from-purple-400 to-pink-400',
    bgColor: 'bg-purple-50',
    description: 'Letting go of what no longer serves'
  },
  embody: {
    name: 'Embody',
    emoji: '✨',
    color: 'from-pink-400 to-rose-400',
    bgColor: 'bg-pink-50',
    description: 'Becoming your future self'
  },
  integrate: {
    name: 'Integrate',
    emoji: '🌟',
    color: 'from-cyan-400 to-blue-400',
    bgColor: 'bg-cyan-50',
    description: 'Making the new normal'
  }
};

const CATEGORY_CONFIG: Record<ManifestationCategory, { emoji: string; label: string }> = {
  career: { emoji: '💼', label: 'Career' },
  love: { emoji: '💕', label: 'Love' },
  health: { emoji: '🌿', label: 'Health' },
  abundance: { emoji: '✨', label: 'Abundance' },
  growth: { emoji: '🌱', label: 'Growth' },
  creativity: { emoji: '🎨', label: 'Creativity' },
  relationships: { emoji: '💫', label: 'Relationships' },
  purpose: { emoji: '🎯', label: 'Purpose' }
};

interface SoulFileDashboardProps {
  embedded?: boolean;
  onClose?: () => void;
}

export function SoulFileDashboard({ embedded = false, onClose }: SoulFileDashboardProps) {
  const { soulFile, loading, updateEssence } = useSoulFile();
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Wrapper class based on embedded mode
  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto pb-8"  // Embedded in chat container
    : "min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-24";

  const centerClass = embedded
    ? "flex items-center justify-center h-full"
    : "min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50";

  if (loading) {
    return (
      <div className={centerClass}>
        <div className="text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-500 animate-pulse" />
          <p className="text-purple-600">Loading your Soul File...</p>
        </div>
      </div>
    );
  }

  if (!soulFile) {
    return (
      <div className={centerClass}>
        <div className="text-center p-8">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-400" />
          <p className="text-purple-600 mb-4">Your Soul File is waiting to be created.</p>
          <p className="text-gray-500 text-sm">Complete the Mirror Session to begin.</p>
        </div>
      </div>
    );
  }

  const { essence, manifestations, journey, mystaraNotes, evidence, stats } = soulFile;
  const phaseConfig = PHASE_CONFIG[journey.currentPhase];
  const activeManifestations = manifestations.filter(m => m.status === 'active');

  return (
    <div className={wrapperClass}>
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent" />
        
        <div className="relative max-w-2xl mx-auto px-4 pt-8">
          {/* Main Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl border border-white/60 overflow-hidden"
          >
            {/* Phase indicator bar */}
            <div className={`h-1.5 bg-gradient-to-r ${phaseConfig.color}`} />
            
            <div className="p-6">
              {/* Name and spark */}
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {essence.preferredName || essence.displayName || 'Beautiful Soul'}
                </h1>
                {essence.spark && (
                  <p className="text-purple-600 italic">"{essence.spark}"</p>
                )}
              </div>

              {/* Current Phase */}
              <div className={`${phaseConfig.bgColor} rounded-2xl p-4 mb-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{phaseConfig.emoji}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{phaseConfig.name} Phase</p>
                      <p className="text-sm text-gray-600">{phaseConfig.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{journey.phaseProgress}%</p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-white/60 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${journey.phaseProgress}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    className={`h-full bg-gradient-to-r ${phaseConfig.color} rounded-full`}
                  />
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  icon={<Flame className="w-5 h-5 text-orange-500" />}
                  value={stats.currentStreak}
                  label="Days Showing Up"
                  highlight={stats.currentStreak >= 7}
                />
                <StatCard
                  icon={<Zap className="w-5 h-5 text-purple-500" />}
                  value={stats.totalEvidence}
                  label="Evidence"
                />
                <StatCard
                  icon={<Award className="w-5 h-5 text-pink-500" />}
                  value={journey.keyBreakthroughs.length}
                  label="Breakthroughs"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-2xl mx-auto px-4 mt-6 space-y-4">
        
        {/* Active Manifestations */}
        <CollapsibleSection
          title="What I'm Calling In"
          emoji="🌙"
          isOpen={activeSection === 'manifestations'}
          onToggle={() => setActiveSection(activeSection === 'manifestations' ? null : 'manifestations')}
          badge={activeManifestations.length > 0 ? `${activeManifestations.length} active` : undefined}
        >
          {activeManifestations.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No active manifestations yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeManifestations.map(m => (
                <div key={m.id} className="bg-white/60 rounded-xl p-4 border border-white/80">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{CATEGORY_CONFIG[m.category]?.emoji || '✨'}</span>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{m.title}</h4>
                      {m.feelingUnderneath && (
                        <p className="text-sm text-purple-600 mt-1">
                          Seeking: {m.feelingUnderneath}
                        </p>
                      )}
                      {m.identityStatement && (
                        <p className="text-sm text-gray-600 mt-2 italic">
                          "{m.identityStatement}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* The Essence */}
        <CollapsibleSection
          title="Who I Am"
          emoji="💜"
          isOpen={activeSection === 'essence'}
          onToggle={() => setActiveSection(activeSection === 'essence' ? null : 'essence')}
        >
          <div className="space-y-4">
            {essence.coreValues.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Core Values</p>
                <div className="flex flex-wrap gap-2">
                  {essence.coreValues.map((value, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {essence.gifts.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">My Gifts</p>
                <div className="flex flex-wrap gap-2">
                  {essence.gifts.map((gift, i) => (
                    <span key={i} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">
                      {gift}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {essence.growthEdges.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Growth Edges</p>
                <div className="flex flex-wrap gap-2">
                  {essence.growthEdges.map((edge, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                      {edge}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Communication Style</p>
              <p className="text-gray-600 capitalize">{essence.communicationStyle}</p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Mystara's Notes */}
        <CollapsibleSection
          title="What Mystara Sees"
          emoji="👁️"
          isOpen={activeSection === 'notes'}
          onToggle={() => setActiveSection(activeSection === 'notes' ? null : 'notes')}
        >
          <div className="space-y-4">
            {mystaraNotes.superpowers.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Your Superpowers</p>
                <ul className="space-y-1">
                  {mystaraNotes.superpowers.map((power, i) => (
                    <li key={i} className="text-gray-600 flex items-start gap-2">
                      <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      {power}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {mystaraNotes.observedPatterns.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Patterns I've Noticed</p>
                <ul className="space-y-1">
                  {mystaraNotes.observedPatterns.map((pattern, i) => (
                    <li key={i} className="text-gray-600 text-sm">{pattern}</li>
                  ))}
                </ul>
              </div>
            )}

            {mystaraNotes.lightUpTopics.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">What Lights You Up</p>
                <div className="flex flex-wrap gap-2">
                  {mystaraNotes.lightUpTopics.map((topic, i) => (
                    <span key={i} className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                      ✨ {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {mystaraNotes.superpowers.length === 0 && 
             mystaraNotes.observedPatterns.length === 0 && 
             mystaraNotes.lightUpTopics.length === 0 && (
              <p className="text-gray-500 text-center py-4">
                Keep chatting with Mystara — she's still getting to know you ✨
              </p>
            )}
          </div>
        </CollapsibleSection>

        {/* Evidence Bank */}
        <CollapsibleSection
          title="Evidence of My Growth"
          emoji="💎"
          isOpen={activeSection === 'evidence'}
          onToggle={() => setActiveSection(activeSection === 'evidence' ? null : 'evidence')}
          badge={evidence.length > 0 ? `${evidence.length} collected` : undefined}
        >
          {evidence.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No evidence collected yet</p>
              <p className="text-sm mt-1">Your transformation proof will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {evidence.slice(0, 5).map(e => (
                <div 
                  key={e.id} 
                  className={`bg-white/60 rounded-xl p-4 border ${
                    e.isHighlight ? 'border-amber-300 bg-amber-50/50' : 'border-white/80'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <EvidenceIcon category={e.category} />
                    <div className="flex-1">
                      <p className="text-gray-800">{e.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(e.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    {e.isHighlight && <Star className="w-4 h-4 text-amber-500" />}
                  </div>
                </div>
              ))}
              {evidence.length > 5 && (
                <button className="w-full text-center text-purple-600 text-sm py-2 hover:text-purple-700">
                  View all {evidence.length} pieces of evidence →
                </button>
              )}
            </div>
          )}
        </CollapsibleSection>

        {/* Journey Stats */}
        <CollapsibleSection
          title="My Journey"
          emoji="🗺️"
          isOpen={activeSection === 'journey'}
          onToggle={() => setActiveSection(activeSection === 'journey' ? null : 'journey')}
        >
          <div className="grid grid-cols-2 gap-4">
            <JourneyStatCard label="Days with Mystara" value={stats.totalDaysWithMystara} />
            <JourneyStatCard label="Days in a Row" value={`${stats.longestStreak} days`} />
            <JourneyStatCard label="Conversations" value={stats.totalConversations} />
            <JourneyStatCard label="Card Pulls" value={stats.cardPulls} />
            <JourneyStatCard label="Rituals" value={stats.ritualsCompleted} />
            <JourneyStatCard label="Breakthroughs" value={stats.breakthroughCount} />
          </div>

          {journey.keyBreakthroughs.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Key Breakthroughs</p>
              <div className="space-y-2">
                {journey.keyBreakthroughs.slice(0, 3).map(b => (
                  <div key={b.id} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3">
                    <p className="text-gray-800 text-sm">{b.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {PHASE_CONFIG[b.phase].emoji} {PHASE_CONFIG[b.phase].name} Phase
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {journey.identityStatements.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Identity Statements</p>
              <div className="space-y-2">
                {journey.identityStatements.map((statement, i) => (
                  <p key={i} className="text-purple-700 italic text-sm">"{statement}"</p>
                ))}
              </div>
            </div>
          )}
        </CollapsibleSection>
      </div>
    </div>
  );
}

// Helper Components

function StatCard({ icon, value, label, highlight }: { 
  icon: React.ReactNode; 
  value: number | string; 
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className={`text-center p-3 rounded-xl ${highlight ? 'bg-gradient-to-br from-orange-100 to-amber-100' : 'bg-gray-50'}`}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600">{label}</p>
    </div>
  );
}

function CollapsibleSection({ 
  title, 
  emoji, 
  children, 
  isOpen, 
  onToggle,
  badge
}: { 
  title: string; 
  emoji: string; 
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  badge?: string;
}) {
  return (
    <motion.div 
      className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 overflow-hidden"
      initial={false}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{emoji}</span>
          <span className="font-medium text-gray-900">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs">
              {badge}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function JourneyStatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white/60 rounded-xl p-3 text-center">
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600">{label}</p>
    </div>
  );
}

function EvidenceIcon({ category }: { category: string }) {
  const icons: Record<string, string> = {
    identity: '🦋',
    manifestation: '✨',
    synchronicity: '🔮',
    feeling: '💜',
    'pattern-break': '⚡'
  };
  return <span className="text-xl">{icons[category] || '💎'}</span>;
}

export default SoulFileDashboard;
